import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  IndianRupee, 
  Plus, 
  Edit, 
  Trash2, 
  Clock, 
  Calendar,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  Filter,
  RefreshCw,
  Building2,
  Gamepad2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

// Day type icons and colors
const DAY_TYPE_CONFIG = {
  'weekday': { label: 'Weekdays', icon: Calendar, color: 'bg-blue-100 text-blue-700' },
  'weekend': { label: 'Weekend', icon: Sun, color: 'bg-amber-100 text-amber-700' },
  'all': { label: 'All Days', icon: Calendar, color: 'bg-purple-100 text-purple-700' }
};

// Time period icons
const TIME_PERIOD_CONFIG = {
  morning: { label: 'Morning', icon: Sunrise, color: 'text-amber-500' },
  afternoon: { label: 'Afternoon', icon: Sun, color: 'text-orange-500' },
  evening: { label: 'Evening', icon: Sunset, color: 'text-rose-500' },
  night: { label: 'Night', icon: Moon, color: 'text-indigo-500' }
};

// Time slot options (30-min intervals)
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h % 12 || 12;
    const period = h < 12 ? 'AM' : 'PM';
    const time = `${hour}:${m.toString().padStart(2, '0')} ${period}`;
    TIME_SLOTS.push(time);
  }
}

export default function PricingDashboardPage() {
  const { admin } = useAuth();
  const [pricingRules, setPricingRules] = useState([]);
  const [courts, setCourts] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterVenue, setFilterVenue] = useState('all');
  const [filterCourt, setFilterCourt] = useState('all');
  const [filterGame, setFilterGame] = useState('all');
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(null);
  
  // Grouped view
  const [groupedView, setGroupedView] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState([]);
  
  // Form state
  const [form, setForm] = useState({
    court_id: '',
    game: '',
    day_type: 'all',
    time_start: '6:00 AM',
    time_end: '10:00 PM',
    price_per_slot: 300,
    effective_from: new Date().toISOString().split('T')[0],
    effective_until: '',
    is_active: true
  });

  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';
  const allGames = [...new Set(courts.flatMap(c => c.games || []))];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Auto-expand all groups on initial load
    if (pricingRules.length > 0 && expandedGroups.length === 0) {
      const groups = [...new Set(pricingRules.map(r => `${r.court_id}-${r.game}`))];
      setExpandedGroups(groups);
    }
  }, [pricingRules]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricingRes, venuesRes] = await Promise.all([
        axios.get(`${API}/masters/court-pricing`),
        axios.get(`${API}/masters/venues-with-courts`)
      ]);
      
      setPricingRules(pricingRes.data.pricing_rules || []);
      setVenues(venuesRes.data.venues || []);
      
      // Flatten courts from venues
      const allCourts = [];
      (venuesRes.data.venues || []).forEach(venue => {
        (venue.courts || []).forEach(court => {
          allCourts.push({
            ...court,
            venue_id: venue.id,
            venue_name: venue.name
          });
        });
      });
      setCourts(allCourts);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredRules = () => {
    return pricingRules.filter(rule => {
      if (filterVenue !== 'all') {
        const court = courts.find(c => c.id === rule.court_id);
        if (!court || court.venue_id !== filterVenue) return false;
      }
      if (filterCourt !== 'all' && rule.court_id !== filterCourt) return false;
      if (filterGame !== 'all' && rule.game !== filterGame) return false;
      return true;
    });
  };

  const getGroupedRules = () => {
    const filtered = getFilteredRules();
    const groups = {};
    
    filtered.forEach(rule => {
      const key = `${rule.court_id}-${rule.game}`;
      if (!groups[key]) {
        const court = courts.find(c => c.id === rule.court_id);
        groups[key] = {
          court_id: rule.court_id,
          court_name: court?.name || 'Unknown Court',
          venue_name: court?.venue_name || 'Unknown Venue',
          game: rule.game,
          rules: []
        };
      }
      groups[key].rules.push(rule);
    });
    
    return Object.values(groups);
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => 
      prev.includes(key) 
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleCreateRule = () => {
    setEditingRule(null);
    setForm({
      court_id: courts[0]?.id || '',
      game: '',
      day_type: 'all',
      time_start: '6:00 AM',
      time_end: '10:00 PM',
      price_per_slot: 300,
      effective_from: new Date().toISOString().split('T')[0],
      effective_until: '',
      is_active: true
    });
    setModalOpen(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setForm({
      court_id: rule.court_id,
      game: rule.game,
      day_type: rule.day_type,
      time_start: rule.time_start,
      time_end: rule.time_end,
      price_per_slot: rule.price_per_slot,
      effective_from: rule.effective_from || '',
      effective_until: rule.effective_until || '',
      is_active: rule.is_active
    });
    setModalOpen(true);
  };

  const handleDeleteClick = (rule) => {
    setDeletingRule(rule);
    setDeleteDialogOpen(true);
  };

  const handleSaveRule = async () => {
    if (!form.court_id || !form.game) {
      toast.error('Please select a court and game');
      return;
    }
    
    if (form.price_per_slot < 0) {
      toast.error('Price cannot be negative');
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API}/masters/court-pricing/${editingRule.id}`, form);
        toast.success('Pricing rule updated');
      } else {
        await axios.post(`${API}/masters/court-pricing`, form);
        toast.success('Pricing rule created');
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save pricing rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to save pricing rule');
    }
  };

  const handleDeleteRule = async () => {
    if (!deletingRule) return;
    
    try {
      await axios.delete(`${API}/masters/court-pricing/${deletingRule.id}`);
      toast.success('Pricing rule deleted');
      setDeleteDialogOpen(false);
      setDeletingRule(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete pricing rule:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete pricing rule');
    }
  };

  const getCourtGames = (courtId) => {
    const court = courts.find(c => c.id === courtId);
    return court?.games || [];
  };

  const getTimePeriod = (timeStr) => {
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return 'morning';
    let hour = parseInt(match[1]);
    const period = match[3].toUpperCase();
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    if (hour >= 6 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  };

  const filteredCourts = filterVenue === 'all' 
    ? courts 
    : courts.filter(c => c.venue_id === filterVenue);

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <IndianRupee className="w-7 h-7 text-emerald-600" />
              Pricing Dashboard
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Configure day/time-based pricing rules for courts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-pricing"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {isAdmin && (
              <Button
                onClick={handleCreateRule}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-pricing-rule"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Pricing Rule
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters:</span>
            </div>
            
            <Select value={filterVenue} onValueChange={setFilterVenue}>
              <SelectTrigger className="w-[180px]" data-testid="filter-venue">
                <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                <SelectValue placeholder="All Venues" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {venues.map(venue => (
                  <SelectItem key={venue.id} value={venue.id}>{venue.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterCourt} onValueChange={setFilterCourt}>
              <SelectTrigger className="w-[180px]" data-testid="filter-court">
                <Gamepad2 className="w-4 h-4 mr-2 text-slate-500" />
                <SelectValue placeholder="All Courts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courts</SelectItem>
                {filteredCourts.map(court => (
                  <SelectItem key={court.id} value={court.id}>
                    {court.name} ({court.venue_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterGame} onValueChange={setFilterGame}>
              <SelectTrigger className="w-[150px]" data-testid="filter-game">
                <SelectValue placeholder="All Games" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Games</SelectItem>
                {allGames.map(game => (
                  <SelectItem key={game} value={game}>{game}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-slate-500">Grouped View</span>
              <Switch 
                checked={groupedView} 
                onCheckedChange={setGroupedView}
                data-testid="toggle-grouped-view"
              />
            </div>
          </div>
        </Card>

        {/* Pricing Rules */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
          </div>
        ) : getFilteredRules().length === 0 ? (
          <Card className="p-12 text-center">
            <IndianRupee className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Pricing Rules Found</h3>
            <p className="text-slate-500 mb-4">
              {pricingRules.length === 0 
                ? 'Create pricing rules to configure dynamic slot pricing'
                : 'No rules match your current filters'}
            </p>
            {isAdmin && pricingRules.length === 0 && (
              <Button onClick={handleCreateRule} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-1" />
                Add First Rule
              </Button>
            )}
          </Card>
        ) : groupedView ? (
          // Grouped View
          <div className="space-y-4">
            {getGroupedRules().map((group) => {
              const groupKey = `${group.court_id}-${group.game}`;
              const isExpanded = expandedGroups.includes(groupKey);
              
              return (
                <Card key={groupKey} className="overflow-hidden">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <IndianRupee className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">
                          {group.court_name} - {group.game}
                        </p>
                        <p className="text-sm text-slate-500">{group.venue_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">
                        {group.rules.length} rule{group.rules.length !== 1 ? 's' : ''}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </button>
                  
                  {/* Rules List */}
                  {isExpanded && (
                    <div className="p-4 space-y-2">
                      {group.rules.map((rule) => (
                        <PricingRuleCard 
                          key={rule.id} 
                          rule={rule} 
                          onEdit={() => handleEditRule(rule)}
                          onDelete={() => handleDeleteClick(rule)}
                          isAdmin={isAdmin}
                          getTimePeriod={getTimePeriod}
                        />
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ) : (
          // Flat View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getFilteredRules().map((rule) => {
              const court = courts.find(c => c.id === rule.court_id);
              return (
                <Card key={rule.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">{court?.name || 'Unknown'}</p>
                      <p className="text-sm text-slate-500">{rule.game}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEditRule(rule)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteClick(rule)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <PricingRuleDetails rule={rule} getTimePeriod={getTimePeriod} />
                </Card>
              );
            })}
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <p className="text-sm text-emerald-600">Total Rules</p>
            <p className="text-2xl font-bold text-emerald-700">{pricingRules.length}</p>
          </Card>
          <Card className="p-4 bg-blue-50 border-blue-200">
            <p className="text-sm text-blue-600">Active Rules</p>
            <p className="text-2xl font-bold text-blue-700">
              {pricingRules.filter(r => r.is_active).length}
            </p>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <p className="text-sm text-amber-600">Courts with Pricing</p>
            <p className="text-2xl font-bold text-amber-700">
              {new Set(pricingRules.map(r => r.court_id)).size}
            </p>
          </Card>
          <Card className="p-4 bg-purple-50 border-purple-200">
            <p className="text-sm text-purple-600">Games Configured</p>
            <p className="text-2xl font-bold text-purple-700">
              {new Set(pricingRules.map(r => r.game)).size}
            </p>
          </Card>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? 'Edit Pricing Rule' : 'Create Pricing Rule'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Court Selection */}
            <div className="space-y-2">
              <Label>Court *</Label>
              <Select 
                value={form.court_id} 
                onValueChange={(val) => {
                  setForm({ ...form, court_id: val, game: '' });
                }}
              >
                <SelectTrigger data-testid="select-court">
                  <SelectValue placeholder="Select a court" />
                </SelectTrigger>
                <SelectContent>
                  {courts.map(court => (
                    <SelectItem key={court.id} value={court.id}>
                      {court.name} ({court.venue_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Game Selection */}
            <div className="space-y-2">
              <Label>Game *</Label>
              <Select 
                value={form.game} 
                onValueChange={(val) => setForm({ ...form, game: val })}
                disabled={!form.court_id}
              >
                <SelectTrigger data-testid="select-game">
                  <SelectValue placeholder="Select a game" />
                </SelectTrigger>
                <SelectContent>
                  {getCourtGames(form.court_id).map(game => (
                    <SelectItem key={game} value={game}>{game}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Day Type */}
            <div className="space-y-2">
              <Label>Day Type</Label>
              <Select 
                value={form.day_type} 
                onValueChange={(val) => setForm({ ...form, day_type: val })}
              >
                <SelectTrigger data-testid="select-day-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days</SelectItem>
                  <SelectItem value="weekday">Weekdays (Mon-Fri)</SelectItem>
                  <SelectItem value="weekend">Weekend (Sat-Sun)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Select 
                  value={form.time_start} 
                  onValueChange={(val) => setForm({ ...form, time_start: val })}
                >
                  <SelectTrigger data-testid="select-time-start">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Select 
                  value={form.time_end} 
                  onValueChange={(val) => setForm({ ...form, time_end: val })}
                >
                  <SelectTrigger data-testid="select-time-end">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {TIME_SLOTS.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>Price per 30-min Slot (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="number"
                  value={form.price_per_slot}
                  onChange={(e) => setForm({ ...form, price_per_slot: parseFloat(e.target.value) || 0 })}
                  className="pl-9"
                  min="0"
                  step="50"
                  data-testid="input-price"
                />
              </div>
            </div>

            {/* Effective Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective From</Label>
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  data-testid="input-effective-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Effective Until (Optional)</Label>
                <Input
                  type="date"
                  value={form.effective_until}
                  onChange={(e) => setForm({ ...form, effective_until: e.target.value })}
                  data-testid="input-effective-until"
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch 
                checked={form.is_active} 
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                data-testid="toggle-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveRule}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-pricing-rule"
            >
              {editingRule ? 'Update' : 'Create'} Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pricing Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this pricing rule. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteRule}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

// Sub-component for rule card in grouped view
function PricingRuleCard({ rule, onEdit, onDelete, isAdmin, getTimePeriod }) {
  const dayConfig = DAY_TYPE_CONFIG[rule.day_type] || DAY_TYPE_CONFIG['all'];
  const DayIcon = dayConfig.icon;
  const timePeriod = getTimePeriod(rule.time_start);
  const TimeIcon = TIME_PERIOD_CONFIG[timePeriod]?.icon || Clock;
  
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      rule.is_active 
        ? 'bg-white border-slate-200' 
        : 'bg-slate-50 border-slate-200 opacity-60'
    }`}>
      <div className="flex items-center gap-4">
        {/* Day Type Badge */}
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${dayConfig.color}`}>
          <DayIcon className="w-3 h-3" />
          {dayConfig.label}
        </span>
        
        {/* Time Range */}
        <div className="flex items-center gap-1 text-sm text-slate-600">
          <TimeIcon className={`w-4 h-4 ${TIME_PERIOD_CONFIG[timePeriod]?.color}`} />
          <span>{rule.time_start} - {rule.time_end}</span>
        </div>
        
        {/* Price */}
        <div className="flex items-center gap-1 font-semibold text-emerald-700">
          <IndianRupee className="w-4 h-4" />
          <span>{rule.price_per_slot}</span>
          <span className="text-xs text-slate-500 font-normal">/slot</span>
        </div>
        
        {!rule.is_active && (
          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
            Inactive
          </span>
        )}
      </div>
      
      {isAdmin && (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={onEdit}
            data-testid={`edit-rule-${rule.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={onDelete}
            data-testid={`delete-rule-${rule.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// Sub-component for rule details in flat view
function PricingRuleDetails({ rule, getTimePeriod }) {
  const dayConfig = DAY_TYPE_CONFIG[rule.day_type] || DAY_TYPE_CONFIG['all'];
  const DayIcon = dayConfig.icon;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${dayConfig.color}`}>
          <DayIcon className="w-3 h-3" />
          {dayConfig.label}
        </span>
        {!rule.is_active && (
          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
            Inactive
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Clock className="w-4 h-4" />
        <span>{rule.time_start} - {rule.time_end}</span>
      </div>
      <div className="flex items-center gap-1 text-lg font-bold text-emerald-700">
        <IndianRupee className="w-5 h-5" />
        <span>{rule.price_per_slot}</span>
        <span className="text-sm text-slate-500 font-normal">/30 min slot</span>
      </div>
    </div>
  );
}
